import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import {
  addLeveledRow, computeAveragePercent, createDeliverable, getMyDeliverable,
  listDeliverableRows, removeRow, updateDeliverableHeader, updateRow,
} from '../../lib/deliverables';
import type { CourseDeliverable, DeliverableRow } from '../../types/deliverables';
import { DeliverableTable } from './DeliverableTable';
import { Spinner } from '../ui/Spinner';
import { toast } from '../ui/Toaster';
import { ClipboardCheck, Save } from 'lucide-react';

interface EnrolledCourse {
  id:   string;
  name: string;
}

export function AlumnoView() {
  const { profile } = useAuth();
  const [courses, setCourses]   = useState<EnrolledCourse[]>([]);
  const [courseId, setCourseId] = useState<string>('');
  const [deliv, setDeliv]       = useState<CourseDeliverable | null>(null);
  const [rows, setRows]         = useState<DeliverableRow[]>([]);
  const [loading, setLoading]   = useState(true);

  // ── Carga inicial: cursos del alumno ──
  useEffect(() => {
    if (!profile?.id) return;
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('course_enrollments')
        .select('courses!inner(id, name, status)')
        .eq('alumno_id', profile.id)
        .eq('status', 'active')
        .eq('courses.status', 'active');
      if (error) {
        console.error('[AlumnoView] cursos:', error);
        toast.error('No pude cargar tus cursos');
        setLoading(false);
        return;
      }
      const list = (data ?? []).map((e: any) => ({ id: e.courses.id, name: e.courses.name }));
      setCourses(list);
      if (list.length === 0) {
        setLoading(false);
      } else {
        setCourseId(list[0].id);
      }
    })();
  }, [profile?.id]);

  // ── Cuando cambia el curso, traemos o creamos el entregable ──
  useEffect(() => {
    if (!courseId) return;
    (async () => {
      setLoading(true);
      let d = await getMyDeliverable(courseId);
      if (!d) {
        d = await createDeliverable(courseId);
      }
      if (!d) {
        toast.error('No pude crear el entregable. Reintentá.');
        setLoading(false);
        return;
      }
      setDeliv(d);
      const rs = await listDeliverableRows(d.id);
      setRows(rs);
      setLoading(false);
    })();
  }, [courseId]);

  // 3 barras de progreso: Modelado, Láminas y Total — todas son el
  // promedio del campo ESTADO sobre las filas que aplican.
  const avgModelado = useMemo(() => computeAveragePercent(rows.filter((r) => r.sheet === 'modelado')), [rows]);
  const avgLaminas  = useMemo(() => computeAveragePercent(rows.filter((r) => r.sheet === 'laminas')),  [rows]);
  const avgPercent  = useMemo(() => computeAveragePercent(rows), [rows]);

  // ── Edición del header con debounce manual (al perder foco) ──
  const handleHeaderBlur = async (patch: Partial<CourseDeliverable>) => {
    if (!deliv) return;
    const ok = await updateDeliverableHeader(deliv.id, patch);
    if (!ok) {
      toast.error('No pude guardar los datos');
      return;
    }
    setDeliv({ ...deliv, ...patch });
  };

  // Para notes hacemos save al blur en vez de a cada keystroke (mejor UX)
  const handleNotesChange = (row: DeliverableRow, notes: string) => {
    setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, notes } : r)));
  };

  // Campo "ESTADO" (% manual): optimista contra BD.
  const handlePercentChange = async (row: DeliverableRow, percent: number) => {
    const prev = row.percent_status;
    setRows((rs) => rs.map((r) => (r.id === row.id ? { ...r, percent_status: percent } : r)));
    const ok = await updateRow(row.id, { percent_status: percent });
    if (!ok) {
      setRows((rs) => rs.map((r) => (r.id === row.id ? { ...r, percent_status: prev } : r)));
      toast.error('No pude guardar el estado');
    }
  };

  const handleAddLevel = async (row: DeliverableRow) => {
    // Buscamos el max level del grupo (la última fila del mismo base)
    const baseActivity = row.activity.replace(/\sN\s*\d+\s*$/i, '');
    const groupRows = rows.filter((r) => r.is_leveled && r.activity.replace(/\sN\s*\d+\s*$/i, '') === baseActivity);
    const maxLevel  = Math.max(...groupRows.map((r) => r.level_index ?? 0));
    const newRow = await addLeveledRow(row, maxLevel);
    if (!newRow) {
      toast.error('No pude agregar el nivel');
      return;
    }
    setRows((prev) => [...prev, newRow].sort((a, b) => a.sort_order - b.sort_order));
    toast.success(`Nivel N${newRow.level_index} agregado`);
  };

  const handleRemoveRow = async (row: DeliverableRow) => {
    // Pedimos confirmación porque borra datos
    if (!confirm(`¿Quitar "${row.activity}"?`)) return;
    const ok = await removeRow(row.id);
    if (!ok) {
      toast.error('No pude quitar la fila');
      return;
    }
    setRows((prev) => prev.filter((r) => r.id !== row.id));
  };

  // ── Render ──
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (courses.length === 0) {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <h1 className="text-display text-3xl font-bold text-white mb-2">Entregables</h1>
        <p className="text-muted">
          No estás inscrito en ningún curso activo. Cuando se te asigne un curso vas a poder
          gestionar tu entregable BIM acá.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8 overflow-y-auto">
      {/* Header con título + selector de curso + progreso */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-cubus-lime/10 border border-cubus-lime/30 flex items-center justify-center">
            <ClipboardCheck className="w-6 h-6 text-cubus-lime" />
          </div>
          <div>
            <h1 className="text-display text-2xl lg:text-3xl font-bold text-white">Entregable BIM</h1>
            <p className="text-muted text-sm">Llevá tu control de modelado y láminas dentro de la intranet.</p>
          </div>
        </div>

        {courses.length > 1 && (
          <select
            value={courseId}
            onChange={(e) => setCourseId(e.target.value)}
            className="px-4 py-2 glass-deep border border-white/[0.08] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cubus-lime"
          >
            {courses.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        )}
      </div>

      {/* Encabezado editable + barra de progreso */}
      {deliv && (
        <div className="glass-surface rounded-xl border border-white/[0.06] p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <Field
              label="Nombre del Modelador"
              defaultValue={deliv.student_name ?? profile?.full_name ?? ''}
              onBlur={(v) => handleHeaderBlur({ student_name: v })}
            />
            <Field
              label="Proyecto"
              defaultValue={deliv.project_name ?? ''}
              onBlur={(v) => handleHeaderBlur({ project_name: v })}
            />
            <Field
              label="Código de Proyecto"
              placeholder="XXX.XXX.XXXX.XXX"
              defaultValue={deliv.project_code ?? ''}
              onBlur={(v) => handleHeaderBlur({ project_code: v })}
            />
            <Field
              label="Fecha"
              type="date"
              defaultValue={deliv.project_date ?? ''}
              onBlur={(v) => handleHeaderBlur({ project_date: v || null })}
            />
          </div>

          {/* 3 barras de progreso lado a lado: Modelado, Láminas y Total.
              Cada una es el promedio del campo ESTADO de las filas de su
              hoja (Total = promedio de todas). Mismo degradé rojo→ámbar→lime. */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <ProgressMini label="Modelado"     percent={avgModelado} />
            <ProgressMini label="Láminas"      percent={avgLaminas}  />
            <ProgressMini label="Avance Total" percent={avgPercent}  emphasize />
          </div>
        </div>
      )}

      {/* Tabla principal */}
      <DeliverableTable
        rows={rows}
        editable={true}
        onNotesChange={handleNotesChange}
        onPercentChange={handlePercentChange}
        onAddLevel={handleAddLevel}
        onRemoveRow={handleRemoveRow}
      />

      {/* Botón explícito para guardar notas en BD (las notas hacen state-local
          al tipear y se persisten al hacer click acá o al blur del input) */}
      <NotesSaver rows={rows} setRows={setRows} />
    </div>
  );
}

/**
 * Mini barra de progreso etiquetada — usada en el header del entregable
 * para mostrar Modelado / Láminas / Total en una grilla de 3 columnas.
 */
function ProgressMini({ label, percent, emphasize = false }: { label: string; percent: number; emphasize?: boolean }) {
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className={emphasize ? 'text-white/80 font-semibold' : 'text-white/60'}>{label}</span>
        <span className="text-cubus-lime font-bold">{percent}%</span>
      </div>
      <div className={`rounded-full bg-white/[0.05] overflow-hidden ${emphasize ? 'h-3' : 'h-2'}`}>
        <div
          className="h-full transition-all duration-500"
          style={{
            width: `${percent}%`,
            background: 'linear-gradient(90deg, #ef4444 0%, #f59e0b 50%, #DDFF00 100%)',
          }}
        />
      </div>
    </div>
  );
}

function Field({
  label, defaultValue, onBlur, placeholder, type = 'text',
}: {
  label: string;
  defaultValue: string;
  onBlur: (val: string) => void;
  placeholder?: string;
  type?: string;
}) {
  const [v, setV] = useState(defaultValue);
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[2px] text-white/40 font-semibold">{label}</span>
      <input
        type={type}
        value={v}
        placeholder={placeholder}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => v !== defaultValue && onBlur(v)}
        className="mt-1 w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.08] focus:border-cubus-lime/40 text-white text-sm focus:outline-none"
      />
    </label>
  );
}

/**
 * Componente helper para persistir las notas a Supabase.
 * Las notas se editan en state local (no API call por keystroke). Acá
 * ofrecemos un botón "Guardar notas" y también dispara save automático
 * cuando se pierde el foco del input (delegado al blur del <DeliverableTable>
 * vía una técnica simple: comparamos contra snapshot inicial al unmount/save).
 *
 * Versión sencilla: botón que persiste todas las notas modificadas.
 */
function NotesSaver({ rows, setRows }: { rows: DeliverableRow[]; setRows: (r: DeliverableRow[]) => void }) {
  const [snapshot, setSnapshot] = useState<Map<string, string | null>>(new Map());

  // Cuando cambian las rows (carga inicial), tomamos snapshot
  useEffect(() => {
    const m = new Map<string, string | null>();
    for (const r of rows) m.set(r.id, r.notes ?? null);
    setSnapshot(m);
    // Solo en mount inicial / cambio de tamaño del array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.length]);

  const dirty = rows.filter((r) => snapshot.has(r.id) && (snapshot.get(r.id) ?? '') !== (r.notes ?? ''));

  if (dirty.length === 0) return null;

  const handleSave = async () => {
    let ok = 0;
    let fail = 0;
    for (const r of dirty) {
      const success = await updateRow(r.id, { notes: r.notes ?? null });
      if (success) ok += 1; else fail += 1;
    }
    if (fail === 0) {
      toast.success(`Guardadas ${ok} anotaciones`);
      // Actualizar snapshot
      const m = new Map(snapshot);
      for (const r of dirty) m.set(r.id, r.notes ?? null);
      setSnapshot(m);
    } else {
      toast.error(`${fail} anotaciones no se pudieron guardar`);
    }
    setRows([...rows]); // forzar re-render
  };

  return (
    <div className="fixed bottom-6 right-6 z-40">
      <button
        type="button"
        onClick={handleSave}
        className="flex items-center gap-2 px-4 py-3 rounded-xl bg-cubus-lime text-cubus-bg font-semibold shadow-2xl hover:bg-cubus-lime-dim transition-all"
      >
        <Save className="w-4 h-4" />
        Guardar {dirty.length} {dirty.length === 1 ? 'anotación' : 'anotaciones'}
      </button>
    </div>
  );
}
